import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header, Table, Button, Icon, Label, Modal, Message } from 'react-frontier';
import { arrayMoveImmutable } from 'array-move';
import SortableList, { SortableItem } from 'react-easy-sort';
import { firstBy } from 'thenby';
import { useBreadcrumbs, useUser } from '../Hooks';
import { SurveyQuestionItem, SetLoading } from '../SiteClasses';
import { bindClick, getSurveyTypeName } from '../SiteUtil';
import UserAccess from '../UserAccess';
import Toast from '../Toast';
import API from '../API';

enum Modals{
	SORT = 1,
	DELETE = 2,
}

var Surveys = ()=>{
	var navigate = useNavigate();
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var canCreate = hasAccess(UserAccess.FORMS.CREATE_FORMS);
	var canEdit = hasAccess(UserAccess.FORMS.EDIT_FORMS);
	var canDelete = hasAccess(UserAccess.FORMS.DELETE_FORMS);
	var [loadError, setLoadError] = useState<string>(null);
	var [questions, setQuestions] = useState<SurveyQuestionItem[]>(null);
	var [shownModal, setShownModal] = useState<number>(null);
	var [sortQuestions, setSortQuestions] = useState<SurveyQuestionItem[]>([]);
	var [sortError, setSortError] = useState<string>(null);
	var [selected, setSelected] = useState<SurveyQuestionItem>(null);
	var [deleteError, setDeleteError] = useState<string>(null);

	useEffect(()=>{
		setTitle('Encuesta');
		get();
	}, []);

	var get = ()=>{
		API.getSurveyQuestions().then(res=>{
			if(res.error) return setLoadError(res.message);
			setQuestions(res.data);
		}).catch(()=>{
			setLoadError('Hubo un error al obtener las preguntas de la encuesta. (SVL-1)');
		});
	};

	var showSort = ()=>{
		setSortQuestions(questions);
		setSortError(null);
		setShownModal(Modals.SORT);
	}

	var onSortEnd = (old: number, newix: number)=>{
		setSortQuestions(arr=>arrayMoveImmutable(arr, old, newix));
	}

	var saveSort = (setLoadingSort: SetLoading)=>{
		var order = sortQuestions.map(a=>a.question_id);
		setSortError(null);
		setLoadingSort(true);
		API.saveSurveySort(order).then(res=>{
			if(res.error) return setSortError(res.message);
			var qs = [...questions];
			for(var i of qs){
				var ix = order.findIndex(a=>a===i.question_id);
				i.sort_order = ix === -1 ? 0 : order.length - ix;
			}
			setQuestions(qs);
			setShownModal(null);
		}).catch(()=>{
			setSortError('Hubo un error actualizando el orden (SVS-1)');
		}).finally(()=>{
			setLoadingSort(false);
		});
	}

	var onToggleDelete = (question: SurveyQuestionItem)=>{
		setSelected(question);
		setDeleteError(null);
		setShownModal(Modals.DELETE);
	}

	var submitToggleDelete = (setLoadingDelete: SetLoading)=>{
		setDeleteError(null);
		setLoadingDelete(true);
		var is_restore = !!selected.deleted;
		API.deleteSurveyQuestion(selected.question_id, !is_restore).then(res=>{
			if(res.error) return setDeleteError(res.message);
			Toast.success(is_restore ? 'Pregunta restaurada exitosamente' : 'Pregunta eliminada exitosamente');
			setQuestions(prev=>prev.map(q=>q.question_id === selected.question_id ? { ...q, deleted: !is_restore } : q));
			setShownModal(null);
		}).catch(()=>{
			setDeleteError(`Hubo un error inesperado ${is_restore ? 'restaurando' : 'eliminando'} la pregunta. (SVD-1)`);
		}).finally(()=>{
			setLoadingDelete(false);
		});
	}

	if(loadError) return <Header text='Error' subtext={loadError} />;
	if(!questions) return <Header text='Cargando datos' loading />;

	return <div>
		<Table
			style={{ maxWidth: 800, margin: 'auto', marginTop: 20 }}
			striped
		>
			<Table.Head
				title='Preguntas de encuesta'
				titleRight={(canEdit || canCreate) ? (
					<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
						{canEdit && <Button icon iconName='sort' size='small' onClick={showSort} />}
						{canCreate && <Button iconName='plus' color='black' text='Agregar' size='small' as={Link} to='/surveys/create' />}
					</div>
				) : undefined}
			>
				<Table.Row>
					<Table.Cell header collapsing>ID</Table.Cell>
					<Table.Cell header >Pregunta</Table.Cell>
					<Table.Cell header collapsing >Tipo</Table.Cell>
					<Table.Cell header collapsing >Obligatoria</Table.Cell>
					<Table.Cell header collapsing centered>Estado</Table.Cell>
					<Table.Cell header collapsing centered><Icon name='wrench' /></Table.Cell>
				</Table.Row>
			</Table.Head>
			<Table.Body>
				{questions.length === 0 ? (
					<Table.Row>
						<Table.Cell colSpan={6} centered>
							No hay preguntas en la encuesta
						</Table.Cell>
					</Table.Row>
				) : (
					questions.sort(firstBy('sort_order', 'desc')).map(q=>(
						<Table.Row key={q.question_id}>
							<Table.Cell collapsing>{q.question_id}</Table.Cell>
							<Table.Cell>
								<div>{q.question}</div>
								{!!q.question_comment && <div style={{ fontSize: 12, color: '#888' }}>{q.question_comment}</div>}
							</Table.Cell>
							<Table.Cell collapsing>{getSurveyTypeName(q.type_id)}</Table.Cell>
							<Table.Cell collapsing centered>
								{q.required ? <Icon name='check' color='#21ba45' /> : <Icon name='minus' />}
							</Table.Cell>
							<Table.Cell collapsing>
								<Label color={q.deleted ? 'red' : 'green'}>{q.deleted ? 'Inactiva' : 'Activa'}</Label>
							</Table.Cell>
							<Table.Cell collapsing  className='no-select'>
								{(canEdit || canDelete) ? (
									<Button basic icon iconName='ellipsis' size='small' menu={[
										{ text: 'Editar', iconName: 'pen', onClick: ()=>navigate(`/surveys/${q.question_id}`), if: canEdit },
										q.deleted
											? { text: 'Restaurar', iconName: 'trash-restore', onClick: ()=>onToggleDelete(q), if: canDelete }
											: { text: 'Eliminar', iconName: 'trash', onClick: ()=>onToggleDelete(q), if: canDelete },
									]} />
								) : <Icon name='minus' />}
							</Table.Cell>
						</Table.Row>
					))
				)}
			</Table.Body>
		</Table>
		<Modal open={shownModal===Modals.SORT} className='sort-forms-list' onClose={bindClick(setShownModal, null)}>
			<Modal.Header text='Ordenar preguntas' />
			<Modal.Content style={{ position: 'relative', zIndex: 1, maxHeight: 600, overflowY: 'auto', overflowX: 'hidden' }}>
				{!!sortError && <Message type='error' list={[sortError]} style={{ marginBottom: 10 }} />}
				<SortableList
					onSortEnd={onSortEnd}
					className='list'
					draggedItemClassName='sort-forms-list-item-dragged'
				>
					<style>{`.sort-forms-list-item-dragged { z-index: 9999 !important; }`}</style>
					{sortQuestions.map(a=>(
						<SortableItem key={`SRT-SVQ-${a.question_id}`}>
							<div className='item' style={{ padding: 10, marginBottom: 5, backgroundColor: '#f5f5f5', borderRadius: 4, cursor: 'move' }}>
								<div style={{ fontWeight: 'bold' }}>{a.question}</div>
								<div style={{ fontSize: '0.9em', color: '#666' }}>{getSurveyTypeName(a.type_id)}</div>
							</div>
						</SortableItem>
					))}
				</SortableList>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button text='Guardar' color='black' size='small' onClick={saveSort} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.DELETE} onClose={bindClick(setShownModal, null)} size='tiny'>
			<Modal.Header text={selected?.deleted ? 'Restaurar pregunta' : 'Eliminar pregunta'} />
			<Modal.Content>
				{!!selected && (
					<Header
						text={selected.deleted ? '¿Restaurar pregunta?' : '¿Eliminar pregunta?'}
						subtext={`Se ${selected.deleted ? 'restaurará' : 'eliminará'} la pregunta "${selected.question}". Las encuestas ya contestadas no cambian.`}
						size='small'
					/>
				)}
				{!!deleteError && <Message type='error' list={[deleteError]} />}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button
					text={selected?.deleted ? 'Restaurar' : 'Eliminar'}
					color={selected?.deleted ? 'black' : 'red'}
					size='small'
					onClick={submitToggleDelete}
				/>
			</Modal.Actions>
		</Modal>
	</div>
}

export default Surveys;
